import type {
  AdminUser,
  AttendancePerson,
  AttendanceRecord,
  LeaveDay,
  LeaveRequest,
} from "@/api/types";
import { classifyDay, eachDateInRange, isRestDay } from "./calendar";
import { orgYear } from "./org-time";
import { LEAVE_TYPE_SHORT } from "./labels";

/**
 * Every figure in this file is derived from real API rows. Nothing is seeded,
 * sampled or back-filled: when the source is empty the derived series is empty
 * too, and the chart shows its empty state rather than a placeholder curve.
 */

export interface DayTally {
  date: string;
  complete: number;
  half: number;
  /** APPROVED leave only — see the note on countKind below. */
  leave: number;
  absent: number;
}

/**
 * Which bucket one classified day falls in.
 *
 * Approved leave gets its own bucket and is kept out of `absent`: a sanctioned
 * day away is not a day somebody failed to turn up, and counting it as one is
 * what made the attendance rate on these pages misleading.
 *
 * PENDING leave deliberately still counts as absent. It is shown as "Leave
 * pending" on the cell and the badge, because the approver looking at the
 * tracker is exactly who needs to see it — but until somebody actually grants
 * it, it is not an authorised absence, and letting an unapproved request lift
 * the attendance rate would make the request itself the fix.
 */
function countKind(tally: Omit<DayTally, "date">, kind: ReturnType<typeof classifyDay>) {
  if (kind === "complete") tally.complete += 1;
  else if (kind === "half") tally.half += 1;
  else if (kind === "leave") tally.leave += 1;
  // A declared day off, and a pending request, land with "no record": none is
  // worked time, and this chart is about days worked.
  else if (kind === "absent" || kind === "inactive" || kind === "leave-pending") tally.absent += 1;
}

/**
 * Per-day attendance split across a set of people, skipping rest days.
 *
 * `leave` is REQUIRED, and deliberately so. It used to be optional, and three of
 * the five call sites quietly left it out — so a day of approved leave fell
 * through `classifyDay` to "absent" and HR Overview reported a lower attendance
 * rate than HR Attendance did for the same people over the same dates. An
 * omission that changes a percentage should not be silent, so the type now
 * forces every caller to answer the question. Pass an empty Map to mean "nobody
 * is on leave", never to mean "I don't know yet".
 */
export function tallyByDay(
  people: AttendancePerson[],
  records: AttendanceRecord[],
  start: string,
  end: string,
  holidays: Map<string, string>,
  leave: Map<string, LeaveDay>,
): DayTally[] {
  const byKey = new Map<string, AttendanceRecord>();
  for (const record of records) byKey.set(`${record.user_id}|${record.date}`, record);

  const holidaySet = new Set(holidays.keys());

  return eachDateInRange(start, end)
    .filter((date) => !isRestDay(date, holidaySet))
    .map((date) => {
      const tally: DayTally = { date, complete: 0, half: 0, leave: 0, absent: 0 };
      for (const person of people) {
        const key = `${person.id}|${date}`;
        countKind(tally, classifyDay(date, byKey.get(key), holidays, leave.get(key)));
      }
      return tally;
    });
}

/**
 * Share of working-day slots that were a full day, as a whole percentage.
 *
 * Approved-leave slots are out of the denominator entirely, exactly as a holiday
 * already was — a team that took sanctioned leave has not thereby dropped its
 * attendance rate.
 */
export function attendanceRate(tallies: DayTally[]): number | null {
  const total = tallies.reduce((sum, t) => sum + t.complete + t.half + t.absent, 0);
  if (total === 0) return null;
  const complete = tallies.reduce((sum, t) => sum + t.complete, 0);
  return Math.round((complete / total) * 100);
}

/** Approved-leave person-days across a range, for the stat tiles. */
export function leaveDayCount(tallies: DayTally[]): number {
  return tallies.reduce((sum, t) => sum + t.leave, 0);
}

export interface TeamAttendance {
  team: string;
  complete: number;
  half: number;
  leave: number;
  absent: number;
}

/** Attendance split per team, for the org-wide stacked comparison. See tallyByDay on why `leave` is required. */
export function tallyByTeam(
  people: AttendancePerson[],
  records: AttendanceRecord[],
  start: string,
  end: string,
  holidays: Map<string, string>,
  leave: Map<string, LeaveDay>,
): TeamAttendance[] {
  const byKey = new Map<string, AttendanceRecord>();
  for (const record of records) byKey.set(`${record.user_id}|${record.date}`, record);

  const holidaySet = new Set(holidays.keys());
  const workingDays = eachDateInRange(start, end).filter((date) => !isRestDay(date, holidaySet));

  const byTeam = new Map<string, TeamAttendance>();
  for (const person of people) {
    const team = person.team_name ?? "Unassigned";
    const entry = byTeam.get(team) ?? { team, complete: 0, half: 0, leave: 0, absent: 0 };
    for (const date of workingDays) {
      const key = `${person.id}|${date}`;
      countKind(entry, classifyDay(date, byKey.get(key), holidays, leave.get(key)));
    }
    byTeam.set(team, entry);
  }
  return [...byTeam.values()].sort((a, b) => a.team.localeCompare(b.team));
}

/** Counts a field across accounts, e.g. headcount per team or per work mode. */
export function countBy<T>(items: T[], pick: (item: T) => string | null | undefined) {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = pick(item) ?? "Unassigned";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/**
 * Leave requests grouped by type, split by decision status.
 *
 * Withdrawn requests get their own bucket rather than being dropped. Silently
 * excluding them would make the chart disagree with the table beside it, and a
 * team taking leave back is a real pattern somebody may want to see.
 */
export function leaveByType(requests: LeaveRequest[]) {
  const byType = new Map<
    string,
    { type: string; approved: number; pending: number; rejected: number; withdrawn: number }
  >();
  for (const request of requests) {
    const label = LEAVE_TYPE_SHORT[request.leave_type] ?? request.leave_type;
    const entry =
      byType.get(label) ?? { type: label, approved: 0, pending: 0, rejected: 0, withdrawn: 0 };
    entry[request.status] += 1;
    byType.set(label, entry);
  }
  return [...byType.values()].sort(
    (a, b) =>
      b.approved + b.pending + b.rejected + b.withdrawn -
        (a.approved + a.pending + a.rejected + a.withdrawn) || a.type.localeCompare(b.type),
  );
}

/**
 * Approved leave DAYS inside one calendar year — what every "this year" tile
 * claims to show.
 *
 * The tiles used to sum every approved request the endpoint returned, with no
 * year filter at all, under a label that said "this year": from year two they
 * carried the previous year's approvals forward forever and disagreed with the
 * chart beside them. A span is attributed to the year it STARTS in, which is the
 * same rule leaveDaysByMonth uses, so the two now always agree.
 */
export function approvedDaysInYear(requests: LeaveRequest[], year = orgYear()): number {
  return requests
    .filter((r) => r.status === "approved" && r.start_date.startsWith(String(year)))
    .reduce((sum, r) => sum + r.days, 0);
}

/** Approved leave DAYS taken per month of the current year, from real spans. */
export function leaveDaysByMonth(requests: LeaveRequest[], year = orgYear()) {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const totals = new Array(12).fill(0) as number[];
  for (const request of requests) {
    // A fully withdrawn request is excluded by status; a partly withdrawn one is
    // still approved, and `days` has already had the handed-back days taken out
    // server-side, so this needs no separate subtraction.
    if (request.status !== "approved") continue;
    if (!request.start_date.startsWith(String(year))) continue;
    const monthIndex = Number(request.start_date.slice(5, 7)) - 1;
    if (monthIndex < 0 || monthIndex > 11) continue;
    totals[monthIndex] = (totals[monthIndex] ?? 0) + request.days;
  }
  return months.map((label, index) => ({ month: label, days: totals[index] ?? 0 }));
}

/**
 * Approved leave DAYS in one calendar year, grouped by whatever `groupOf` says
 * (a team, a leave type). Same year rule as approvedDaysInYear — a span counts
 * in the year it starts — so the groups add up to that tile's number exactly.
 * Largest first; groups with nothing are left out rather than drawn as zeros.
 */
export function approvedDaysBy(
  requests: LeaveRequest[],
  groupOf: (request: LeaveRequest) => string,
  year = orgYear(),
): { label: string; value: number }[] {
  const totals = new Map<string, number>();
  for (const r of requests) {
    if (r.status !== "approved" || !r.start_date.startsWith(String(year))) continue;
    const label = groupOf(r);
    totals.set(label, (totals.get(label) ?? 0) + r.days);
  }
  return [...totals.entries()]
    .filter(([, value]) => value > 0)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/** Tenure bands. Ordinal, so charts render these with a one-hue ramp. */
export const TENURE_BANDS = ["Under 6 months", "6 to 12 months", "1 to 2 years", "Over 2 years"] as const;

export function tenureDistribution(users: AdminUser[]) {
  const counts = new Map<string, number>(TENURE_BANDS.map((band) => [band, 0]));
  const now = Date.now();
  for (const user of users) {
    if (!user.date_joined) continue;
    const months = (now - new Date(user.date_joined).getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    const band =
      months < 6
        ? TENURE_BANDS[0]
        : months < 12
          ? TENURE_BANDS[1]
          : months < 24
            ? TENURE_BANDS[2]
            : TENURE_BANDS[3];
    counts.set(band, (counts.get(band) ?? 0) + 1);
  }
  return TENURE_BANDS.map((label) => ({ label, value: counts.get(label) ?? 0 }));
}

/** Accounts that have not yet replaced their one-time password. */
export function pendingFirstLogin(users: AdminUser[]): AdminUser[] {
  return users.filter((user) => user.must_reset_password);
}
